import { Suspense, lazy, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppLayout } from './components/AppLayout.js';
import { FullPageMessage } from './components/FullPageMessage.js';
import { RedirectIfAuthenticated, RequireAuth, homePathForRole } from './auth/guards.js';
import { useAuth } from './auth/AuthProvider.js';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { ResetPasswordPage } from './routes/ResetPasswordPage.js';
import { SetupPasswordPage } from './routes/SetupPasswordPage.js';
import { CandidateInterviewPage } from './routes/candidate/CandidateInterviewPage.js';
import { CandidateProfilePage } from './routes/candidate/CandidateProfilePage.js';
import { SystemCheckPage } from './routes/candidate/SystemCheckPage.js';
import { InterviewerDashboardPage } from './routes/interviewer/InterviewerDashboardPage.js';
import { InterviewerInterviewPage } from './routes/interviewer/InterviewerInterviewPage.js';

/**
 * Split out because it is the only route that pulls in LiveKit — roughly two thirds of the
 * bundle. Candidates never reach it at all, and an interviewer only loads it when they
 * actually join a room, so the dashboard stays light.
 */
const SessionPage = lazy(async () => ({
  default: (await import('./routes/interviewer/SessionPage.js')).SessionPage,
}));

/** Sends `/` to whichever home the signed-in role has, or to login when anonymous. */
function RootRedirect(): ReactElement {
  const { status, user } = useAuth();
  if (status === 'bootstrapping') return <FullPageMessage title="Restoring your session…" />;
  return <Navigate to={user ? homePathForRole(user.role) : '/login'} replace />;
}

export function App(): ReactElement {
  return (
    <Routes>
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        {/*
         * The API's invitation emails link to /set-password (see
         * apps/api/src/services/invitation-service.ts). Both spellings are served so an
         * already-sent invitation keeps working.
         */}
        <Route path="/set-password" element={<SetupPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* The path the API's password-reset emails link to (invitation-service.ts). */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/*
       * Outside AppLayout: the live session takes the full viewport (see SessionPage).
       * Admins are allowed in because the API's interviewer routes accept them too.
       */}
      <Route element={<RequireAuth roles={['interviewer', 'admin']} />}>
        <Route
          path="/interviewer/interviews/:interviewId/session"
          element={
            <Suspense fallback={<FullPageMessage title="Loading the session…" />}>
              <SessionPage />
            </Suspense>
          }
        />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route element={<RequireAuth roles={['candidate']} />}>
            <Route path="/candidate" element={<CandidateProfilePage />} />
            <Route path="/candidate/system-check" element={<SystemCheckPage />} />
            <Route path="/candidate/interview/:interviewId" element={<CandidateInterviewPage />} />
          </Route>

          <Route element={<RequireAuth roles={['interviewer', 'admin']} />}>
            <Route path="/interviewer" element={<InterviewerDashboardPage />} />
            <Route
              path="/interviewer/interviews/:interviewId"
              element={<InterviewerInterviewPage />}
            />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route
        path="*"
        element={<FullPageMessage title="Page not found" detail="That page does not exist." />}
      />
    </Routes>
  );
}
